import { PageBlockDefinitionAppearance } from "./aux";
import { PageBlockDefinitionVariant } from "./variant";
import { z } from "zod";
//
// Enums
//
export const PageBlockDefinitionPropertyType = z.enum([
    "RichText",
    "MultiRichText",
    "RichTextEditor",
    "Text",
    "Boolean",
    "Number",
    "SingleSelect",
    "MultiSelect",
    "Image",
    "Token",
    "TokenType",
    "TokenProperty",
    "Component",
    "ComponentProperty",
    "Asset",
    "AssetProperty",
    "FigmaNode",
    "EmbedURL",
    "URL",
    "Markdown",
    "Code",
    "CodeSandbox",
    "Table",
    "Divider",
    "Storybook",
    "Color",
    "FigmaComponent",
    "File",
    "Context",
]);
export const PageBlockDefinitionRichTextPropertyStyle = z.enum([
    "Title1",
    "Title2",
    "Title3",
    "Title4",
    "Title5",
    "Quote",
    "Callout",
    "Default",
]);
export const PageBlockDefinitionMultiRichTextPropertyStyle = z.enum([
    "OL",
    "UL",
]);
export const PageBlockDefinitionRichTextEditorPropertyStyle = z.enum([
    "OL",
    "UL",
    "Bold",
    "Italic",
    "Link",
    "Strikethrough",
    "InlineCode",
]);
export const PageBlockDefinitionTextPropertyStyle = z.enum([
    "Title1",
    "Title2",
    "Title3",
    "Title4",
    "Title5",
    "Default",
    "DefaultBold",
    "DefaultSemibold",
    "Small",
    "SmallBold",
    "SmallSemibold",
    "Custom",
]);
export const PageBlockDefinitionTextPropertyColor = z.enum([
    "Neutral",
    "NeutralFaded",
]);
export const PageBlockDefinitionBooleanPropertyStyle = z.enum([
    "SegmentedControl",
    "ToggleButton",
    "Checkbox",
]);
export const PageBlockDefinitionSingleSelectPropertyStyle = z.enum([
    "SegmentedControl",
    "ToggleButton",
    "Select",
    "Checkbox",
]);
export const PageBlockDefinitionSingleSelectPropertyColor = z.enum([
    "Green",
    "Red",
    "Yellow",
    "Blue",
    "Purple",
    "Orange",
    "Pink",
    "Teal",
    "Brown",
    "Grey",
    "LightGrey",
    "Cyan",
    "Fuchsia",
]);
export const IconSet = z.enum(["CheckCircle", "CrossCircle", "Alert"]);
export const PageBlockDefinitionMultiSelectPropertyStyle = z.enum([
    "SegmentedControl",
    "Select",
    "Checkbox",
]);
export const PageBlockDefinitionImageAspectRatio = z.enum([
    "Auto",
    "Square",
    "Landscape",
    "Portrait",
    "Wide",
]);
export const PageBlockDefinitionImageWidth = z.enum([
    "Full",
    "Icon",
    "Small",
    "Medium",
    "Large",
    "Poster",
]);
//
// Aux
//
export const PageBlockDefinitionSelectChoice = z.object({
    value: z.string(),
    name: z.string(),
    icon: IconSet.optional(),
    customIconUrl: z.string().optional(),
    color: PageBlockDefinitionSingleSelectPropertyColor.optional(),
});
//
// Property options
//
export const PageBlockDefinitionUntypedPropertyOptions = z.record(z.any());
export const PageBlockDefinitionRichTextOptions = z.object({
    richTextStyle: PageBlockDefinitionRichTextPropertyStyle.optional(),
});
export const PageBlockDefinitionMutiRichTextOptions = z.object({
    multiRichTextStyle: PageBlockDefinitionMultiRichTextPropertyStyle.optional(),
});
export const PageBlockDefinitionRichTextEditorOptions = z.object({
    placeholder: z.string().optional(),
    allowedInlineStyles: z
        .array(PageBlockDefinitionRichTextEditorPropertyStyle)
        .optional(),
});
export const PageBlockDefinitionTextOptions = z.object({
    placeholder: z.string().optional(),
    defaultValue: z.string().optional(),
    textStyle: PageBlockDefinitionTextPropertyStyle.optional(),
    color: PageBlockDefinitionTextPropertyColor.optional(),
    allowLineBreaks: z.boolean().optional(),
});
export const PageBlockDefinitionSelectOptions = z.object({
    singleSelectStyle: PageBlockDefinitionSingleSelectPropertyStyle.optional(),
    defaultChoice: z.string(),
    choices: z.array(PageBlockDefinitionSelectChoice),
});
export const PageBlockDefinitionImageOptions = z.object({
    width: PageBlockDefinitionImageWidth.optional(),
    aspectRatio: PageBlockDefinitionImageAspectRatio.optional(),
    allowCaption: z.boolean().optional(),
    recommendation: z.string().optional(),
});
export const PageBlockDefinitionBooleanOptions = z.object({
    defaultvalue: z.boolean().optional(),
    booleanStyle: PageBlockDefinitionBooleanPropertyStyle.optional(),
});
export const PageBlockDefinitionNumberOptions = z.object({
    defaultValue: z.number(),
    min: z.number().optional(),
    max: z.number().optional(),
    step: z.number().optional(),
    placeholder: z.string().optional(),
});
export const PageBlockDefinitionComponentOptions = z.object({
    renderLayoutAs: z.enum(["List", "Table"]).optional(),
    allowPropertySelection: z.boolean().optional(),
});
export const PageBlockDefinitionFigmaComponentOptions = z.object({
    renderLayoutAs: z.enum(["Grid", "PropsTable", "Canvas", "Playground"]),
    invertPropertySelection: z.boolean().optional(),
});
//
// Definitions
//
export const PageBlockDefinitionProperty = z.object({
    id: z.string(),
    name: z.string(),
    type: PageBlockDefinitionPropertyType,
    description: z.string().optional(),
    options: PageBlockDefinitionUntypedPropertyOptions.optional(),
    variantOptions: z
        .record(PageBlockDefinitionUntypedPropertyOptions)
        .optional(),
});
export const PageBlockDefinitionItem = z.object({
    properties: z.array(PageBlockDefinitionProperty),
    appearance: PageBlockDefinitionAppearance.optional(),
    variants: z.array(PageBlockDefinitionVariant),
    defaultVariantKey: z.string(),
});
